import { getDescendants } from '@minoru/react-dnd-treeview';

/**
 * Sorts an array of transformed items based on sidebar positions and categories.
 *
 * The items are sorted first by category (folder, file, api) and then by
 * sidebar position. If an item does not have a sidebar position, it is
 * considered to have a position of Number.MAX_VALUE.
 *
 * @param {Object[]} items - an array of transformed items
 * @param {Object} sidebarPositions - an object with item ids as keys and
 * sidebar positions as values
 * @returns {Object[]} the sorted array of items
 */
export const sortTransformedData = (items, sidebarPositions = {}) => {
  return items.sort((a, b) => {
    const getCategory = (item) => {
      if (item.isApi) return 3;
      if (item.isFile || item.isFolder) return 2; // Treat files and folders equally
      return 0;
    };

    const posA =
      sidebarPositions[a.id] !== undefined
        ? sidebarPositions[a.id]
        : a?.ref_id
          ? sidebarPositions[String(a.ref_id)]
          : Number.MAX_VALUE;
    const posB =
      sidebarPositions[b.id] !== undefined
        ? sidebarPositions[b.id]
        : b.ref_id
          ? sidebarPositions[String(b.ref_id)]
          : Number.MAX_VALUE;

    // Sort by sidebar position first
    if (posA !== posB) return posA - posB;

    const categoryA = getCategory(a);
    const categoryB = getCategory(b);
    if (categoryA !== categoryB) return categoryA - categoryB;

    // If positions are equal or undefined, sort by text
    return a.text.localeCompare(b.text);
  });
};

/**
 * Removes any files that are default files for a folder.
 *
 * This function assumes that the given array of items is sorted by the
 * sidebar position of the items. It goes through the items and keeps track
 * of which files are default files for a folder. If an item is a folder and
 * has a default file, it adds the default file to the 'defaultFiles' object.
 * If an item is not a folder or is not a default file for a folder, it adds
 * the item to the output array.
 *
 * @param {Array} items - The array of items to be filtered.
 * @returns {Array} - The filtered array of items.
 */
export const filterDefaultFolderFile = (items) => {
  const defaultFiles = {};
  const output = [];
  for (let i = 0; i < items.length; i++) {
    const isFolder = !items[i]?.isFile && !items[i]?.isApi;
    if (isFolder && items[i]?.meta_info?.default_file) {
      const defaultFileId = items[i]?.meta_info?.default_file;
      const defaultFileRefId = items[i]?.meta_info?.default_file_ref_id;
      defaultFiles[defaultFileId] = 1;
      defaultFiles[defaultFileRefId] = 1;
      output.push(items[i]);
    } else if (!defaultFiles[items[i].id] || !defaultFiles[items[i].ref_id]) {
      output.push(items[i]);
    }
  }
  return output;
};

/**
 * Transforms the given tree data by taking each item and extracting the
 * children, data, draft, is_draft_published, parent_id_id, and folder_id
 * properties. Then it adds a new 'parent' property with the value of parent_id_id
 * or folder_id, and adds the item to the output array. If the item has
 * subchildren, it recursively applies the transformation to them.
 *
 * Enhanced to use pre-parsed API structures from useOpenApiParser hook for
 * better performance and consistency.
 *
 * @param {Array} tree - The tree data to be transformed.
 * @param {string} rootID - The root ID for the tree structure.
 * @param {Object} openapiTags - Legacy tags object (used for fallback).
 * @param {Object} openapiSpec - The OpenAPI specifications object.
 * @param {Object} options - Configuration options.
 * @param {Object} options.wsMetaInfo - Workspace meta information.
 * @param {boolean} options.initial - Whether this is the initial transformation.
 * @param {Object} options.parsedApiStructures - Pre-parsed API structures from useOpenApiParser.
 * @returns {Array} - The transformed tree data.
 */
export const treeTransformer = (
  tree,
  rootID,
  openapiTags,
  openapiSpec,
  { wsMetaInfo, initial = true, parsedApiStructures = {} } = {}
) => {
  let output = [];
  const size = tree.length;
  for (let i = 0; i < size; i++) {
    const {
      children,
      data,
      markdown_data,
      draft,
      is_draft_published,
      parent_id_id,
      folder_id,
      current,
      title,
      ...rest
    } = tree[i];

    let value = parent_id_id || null;
    const isFile = 'folder_id' in tree[i];

    if (folder_id === null || folder_id) value = folder_id;
    if (value === null) value = rootID;

    const isApi = tree[i]?.type === 'openapi_spec_file' || Object.keys(tree[i]).includes('openapi');
    if (isApi) {
      output.push({
        ...rest,
        id: String(rest?.ref_id),
        uuid_id: rest?.id,
        text: title,
        parent: value,
        isFile: false,
        isApi: true,
        icon: 'folder',
        leaf: false,
      });

      // Use pre-parsed API structure if available, otherwise fall back to manual parsing
      if (parsedApiStructures?.[tree[i]?.ref_id]) {
        const { listData, methodsList } = parsedApiStructures[tree[i]?.ref_id];

        // Add the parsed list data (tags)
        listData.forEach((item) => {
          output.push(item);
        });

        // Add the parsed methods list
        methodsList.forEach((method) => {
          output.push({ ...method, tag: method?.tag ?? 'default' });
        });
      } else if (openapiSpec?.[tree[i]?.ref_id]) {
        // Fallback to manual parsing for backward compatibility
        let isDefaultTagAdded = false;
        openapiTags?.[rest?.ref_id]?.map((tag) => {
          output.push({
            id: `tag-${rest?.ref_id}-${tag}-openApi`,
            uuid_id: rest?.id,
            parent: String(rest?.ref_id),
            text: tag,
            isFile: false,
            isApi: true,
            icon: 'folder',
            leaf: false,
          });
          Object.entries(openapiSpec?.[rest?.ref_id]?.paths || {}).map(([path, methods]) => {
            Object.entries(methods).map(([method, operation]) => {
              let tagParent = `tag-${rest?.ref_id}-${tag}-openApi`;
              if (operation?.tags?.includes(tag)) {
                output.push({
                  apiKey: rest?.ref_id,
                  id: `${rest?.ref_id}-${method}-${path}`,
                  uuid_id: rest?.id,
                  text: operation.summary || path,
                  parent: tagParent,
                  droppable: false,
                  isFile: false,
                  isApi: true,
                  icon: method,
                  leaf: true,
                  method,
                  path,
                  tag: tag ? tag : 'default', // Add tag information for URL generation
                });
              } else if (
                operation?.tags === undefined &&
                output.find((i) => i?.id === `${rest?.id}-${method}-${path}`) === undefined
              ) {
                if (!isDefaultTagAdded) {
                  output.push({
                    uuid_id: rest?.id,
                    id: `tag-${rest?.ref_id}-default-openApi`,
                    parent: rest?.ref_id,
                    text: 'Default',
                    isFile: false,
                    isApi: true,
                    icon: 'folder',
                    leaf: false,
                  });
                  isDefaultTagAdded = true;
                }
                output.push({
                  uuid_id: rest?.id,
                  apiKey: rest?.ref_id,
                  id: `${rest?.ref_id}-${method}-${path}`,
                  text: operation.summary || path,
                  parent: `tag-${rest?.ref_id}-default-openApi`,
                  droppable: false,
                  isFile: false,
                  isApi: true,
                  icon: method,
                  leaf: true,
                  method,
                  path,
                  tag: 'default', // Default tag case - no specific tag
                });
              }
            });
          });
        });
      }
    } else {
      output.push({
        ...rest,
        id: rest?.path || rest?.ref_id || rest?.id,
        uuid_id: rest?.id,
        text: title,
        parent: value,
        droppable: true,
        isFile,
        isApi,
        is_draft_published,
      });
    }
  }
  output = sortTransformedData(filterDefaultFolderFile(output), wsMetaInfo);

  for (let i = 0; i < tree.length; i++) {
    const { children, ...rest } = tree[i];
    if (children && children.length > 0) {
      const subchildren = treeTransformer(children, rest?.path ? rest?.path : rootID, openapiTags, openapiSpec, {
        wsMetaInfo: rest?.meta_info?.sidebar_info,
        initial: false,
        parsedApiStructures,
      });
      output = output.concat(subchildren);
    }
  }
  return output;
};

/**
 * Moves an item in the tree data array from a source index to a target index.
 *
 * This function creates a new array with the specified item relocated, preserving
 * the order of other elements. It is useful for rearranging nodes in a tree structure.
 *
 * @param {Array} data - The array representing tree data.
 * @param {number} sourceIndex - The index of the item to be moved.
 * @param {number} targetIndex - The index where the item should be moved to.
 * @returns {Array} - The new array with the item moved to the target index.
 */

export const sortTreeDataAfterMove = (data, sourceIndex, targetIndex) => {
  const newArray = [...data];
  newArray.splice(targetIndex, 0, newArray.splice(sourceIndex, 1)[0]);
  return newArray;
};

/**
 * Calculates the height of a folder in the tree, taking into account
 * the heights of its children and whether they are droppable or not.
 *
 * @param {number} id - The ID of the folder to calculate the height for.
 * @param {Array} treeData - The array representing the tree structure.
 * @param {Array} openIds - An array of IDs of open folders.
 * @returns {number} - The calculated height of the folder.
 */
export const resolvePipeHeight = (id, treeData, openIds) => {
  treeData = getDescendants(treeData, id);
  const ROW_HEIGHT = 32;
  const LIST_PADDING = 5;

  const droppableHeightExceedsRow = (node) =>
    node?.droppable && openIds.includes(node.id) && treeData.filter((n) => n.parent === node.id).length > 0;

  const getHeightOfId = (id) => {
    const directChildren = treeData.filter((node) => node.parent === id);
    const heightOfChildren = directChildren.map((node) =>
      droppableHeightExceedsRow(node) ? getHeightOfId(node.id) + ROW_HEIGHT + LIST_PADDING : ROW_HEIGHT
    );
    const height = heightOfChildren.reduce((a, b) => a + b, 0);
    return height;
  };

  const lastChild = treeData.filter((node) => node.parent === id).reverse()[0];
  if (droppableHeightExceedsRow(lastChild)) {
    return getHeightOfId(id) - getHeightOfId(lastChild.id) - LIST_PADDING;
  }

  return getHeightOfId(id);
};

/**
 * Determines if a given node in a tree is visible based on the visibility of its parent nodes.
 *
 * The function checks if the node's parent exists in the tree and if it is included
 * in the list of open node IDs. If the parent is visible, it recursively checks
 * the visibility of the parent's parent, ensuring that the entire lineage of nodes
 * is open for the node to be visible. If the node has no parent, it is considered visible.
 *
 * @param {string|number} id - The ID of the node to check for visibility.
 * @param {Array} treeData - The array representing the tree structure.
 * @param {Array} openIds - The array of IDs representing nodes that are open.
 * @returns {boolean} - True if the node is visible, false otherwise.
 */

export const isNodeVisible = (id, treeData, openIds) => {
  const parentId = treeData.find((node) => node.id === id)?.parent;
  const parentExistsInTree = parentId && treeData?.find((node) => node.id === parentId);
  if (parentExistsInTree) {
    const isParentVisible = openIds.includes(parentId);
    return isParentVisible ? isVisible(parentId, treeData) : false;
  } else {
    return true;
  }
};

export const getTransformer = () => treeTransformer;
